/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';
import { ChevronNextIcon, StarFilledIcon } from '@coursera/cds-icons';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import { humanizeDifficultyLevel } from 'bundles/unified-description-page-common/constants';
import type { ArticleRichEntityCardProps } from 'bundles/unified-seo-content-common/components/entity-cards/ArticleRichEntityCard';

import _t from 'i18n!nls/articles';

const styles = {
  card: css`
    min-height: 180px;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    box-sizing: border-box;
    box-shadow: 0 0 10px var(--cds-color-darken-200);
    border-radius: 6px;
    margin: var(--cds-spacing-300) 0;
    padding: var(--cds-spacing-300) var(--cds-spacing-200);
  `,
  image: css`
    height: fit-content;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    box-sizing: border-box;
    border-radius: var(--cds-spacing-50);
    padding: var(--cds-spacing-50);
    margin-right: var(--cds-spacing-300);

    img {
      max-width: 100%;
    }
  `,
  entityType: css`
    text-transform: capitalize;
  `,
  skills: css`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;

    ${breakpoints.down('sm')} {
      -webkit-line-clamp: 2;
      color: var(--cds-color-neutral-primary);

      .skills-title {
        display: inline-block;
      }

      .skills-body {
        display: inline;
        margin-left: var(--cds-spacing-50);
      }
    }
  `,
  header: css`
    display: block;

    svg {
      float: right;
      position: relative;
      bottom: 20px;
    }
  `,
  subtitle: css`
    display: flex;
    margin: var(--cds-spacing-100) 0;

    .mobile-level {
      text-transform: lowercase;
      padding-right: var(--cds-spacing-50);
    }

    .mobile-level::first-letter {
      text-transform: capitalize;
    }
  `,
  ratings: css`
    display: flex;
    margin-top: var(--cds-spacing-150);

    svg {
      margin-top: 2px;
      color: var(--cds-color-emphasis-tertiary-background-default);
    }

    p {
      padding-left: var(--cds-spacing-50);
    }
  `,
};

const MobileEntityCard = ({
  ctaHref,
  ctaAriaLabel,
  imageSrc,
  imageAlt,
  title,
  cardMetaData,
}: ArticleRichEntityCardProps) => {
  const { entityType, level, durationInHours, durationInMonths, skills, averageFiveStarRating, ratingCount } =
    cardMetaData;
  const hasSubtitleSection = !!(level || durationInHours || durationInMonths);
  return (
    <Grid container css={styles.card} data-testid="MobileEntityCard">
      <div css={styles.image}>
        <LazyImgix src={imageSrc} alt={imageAlt} maxWidth={60} maxHeight={60} />
      </div>
      <Grid item container xs>
        <Grid item xs={12}>
          <TrackedLink2
            trackingName="article_rich_product_card"
            withVisibilityTracking
            href={ctaHref}
            aria-label={ctaAriaLabel}
          >
            <Grid item container css={styles.header}>
              <Grid item xs={10}>
                <Typography2 variant="subtitleMedium" component="p">
                  {title}
                </Typography2>
              </Grid>
              <Grid item xs>
                <ChevronNextIcon size="medium" color="interactive" />
              </Grid>
            </Grid>
            <Typography2 css={styles.entityType} variant="subtitleMedium" component="p">
              {entityType}
            </Typography2>
            {hasSubtitleSection && (
              <div css={styles.subtitle}>
                {level && (
                  <Typography2 component="p" variant="bodySecondary" color="supportText" className="mobile-level">
                    {_t('#{level} level', {
                      level: humanizeDifficultyLevel(level),
                    })}
                  </Typography2>
                )}
                {durationInHours && !durationInMonths && (
                  <Typography2 component="p" variant="bodySecondary" color="supportText">
                    {_t('#{separator} #{durationInHours} hour(s)', { durationInHours, separator: level ? '·' : '' })}
                  </Typography2>
                )}
                {durationInMonths && !durationInHours && (
                  <Typography2 component="p" variant="bodySecondary" color="supportText">
                    {_t('#{separator} #{durationInMonths} month(s)', { durationInMonths, separator: level ? '·' : '' })}
                  </Typography2>
                )}
              </div>
            )}
            {skills && (
              <div css={styles.skills}>
                <Typography2 component="p" variant="bodySecondary" className="skills-title">
                  {_t("Skills you'll build:")}
                </Typography2>
                <Typography2 component="p" variant="bodySecondary" color="supportText" className="skills-body">
                  {skills}
                </Typography2>
              </div>
            )}

            <div css={styles.ratings}>
              {!!averageFiveStarRating && (
                <>
                  <StarFilledIcon size="small" />
                  <Typography2 variant="subtitleMedium" component="p">
                    {averageFiveStarRating}
                  </Typography2>
                </>
              )}

              {!!ratingCount && (
                <Typography2 variant="bodySecondary" color="supportText" component="p">
                  {_t('(#{ratingCount} ratings)', {
                    ratingCount: ratingCount.toLocaleString(),
                  })}
                </Typography2>
              )}
            </div>
          </TrackedLink2>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MobileEntityCard;
